/**
 * @typedef PiezometroFromApi
 * @prop {string} subarea
 * @prop {string} idpuntodimisura
 * @prop {string} nome
 * @prop {"Piezómetro"} tipo
 * @prop {string} x_utm
 * @prop {string} y_utm
 * @prop {string} elev_refer_msnm
 * @prop {string} stick_up
 * @prop {string} id_doc
 */

/**
 * @typedef Piezometro
 * @prop {string} id
 * @prop {string} subarea
 * @prop {string} idpuntodimisura
 * @prop {string} nome
 * @prop {"Piezómetro"} tipo
 * @prop {string} x_utm
 * @prop {string} y_utm
 * @prop {string} elev_refer_msnm
 * @prop {string} stick_up
 * @prop {string} id_doc
 */

/**
 * @typedef LevelFromApi
 * @prop {string} IDpuntoDiMisura "65",
 * @prop {string} ID_NIVEL "885812",
 * @prop {string} FECHA_HORA "4/10/2019 00:00:00",
 * @prop {string} PROF_NIVEL "7,50",
 * @prop {string} TIPOLOGIA_MEDICION "M",
 * @prop {string} NE_ND "NE",
 * @prop {string} ID_DOC "30",
 * @prop {string} date "4/10/2019"
 */

/**
 * @typedef Level
 * @prop {string} IDpuntoDiMisura "65",
 * @prop {string} id "885812",
 * @prop {string} FECHA_HORA "4/10/2019 00:00:00",
 * @prop {string} value "7,50",
 * @prop {string} TIPOLOGIA_MEDICION "M",
 * @prop {string} NE_ND "NE",
 * @prop {string} ID_DOC "30",
 * @prop {Date} date "4/10/2019"
 */

/**
 * @typedef ConductivityFromApi
 * @prop {string} IDpuntoDiMisura "65",
 * @prop {string} fecha_ora "6/11/2017 00:00:00",
 * @prop {number} so4 1028.0,
 * @prop {number} ce 0
 */

/**
 * @typedef Conductivity
 * @prop {string} IDpuntoDiMisura "65",
 * @prop {string} fecha_ora "6/11/2017 00:00:00",
 * @prop {number} so4 1028.0,
 * @prop {number} ce 0
 * @prop {Date} date
 */

/**
 * @typedef AcqueFromApi
 * @prop {string} IDpuntoDiMisura "65",
 * @prop {string} FECHA_HORA "12/1/2018 00:00:00",
 * @prop {string} ID_PARAMETRO "129",
 * @prop {string} DESCRIZIONE_PARAMETRO "O2 dissolto [mg/l]",
 * @prop {number} valore 1.28
 */

/**
 * @typedef Acque
 * @prop {string} IDpuntoDiMisura "65",
 * @prop {string} FECHA_HORA "12/1/2018 00:00:00",
 * @prop {string} idParam "129",
 * @prop {string} param "O2 dissolto [mg/l]",
 * @prop {number} value 1.28
 * @prop {Date} date
 */

/**
 * @param {PiezometroFromApi} p
 * @returns {Piezometro}
 */
const toPiezometro = p => ({
    ...p,
    x_utm: parseFloat(p.x_utm).toFixed(0), // remove decimals
    y_utm: parseFloat(p.y_utm).toFixed(0),
    id: btoa('P-' + p.nome)
})

/**
 * @param {LevelFromApi} l
 * @returns {Level}
 */
const toLevel = l => ({
    ...l,
    value: l.PROF_NIVEL,
    date: parseDDMMYYYYDate(l.date)
})

/**
 * @param {ConductivityFromApi} c
 * @returns {Conductivity}
 */
const toConductivity = c => ({
    ...c,
    date: parseDDMMYYYYDate(c.fecha_ora)
})

/**
 * @param {AcqueFromApi} a
 * @returns {Acque}
 */
const toAcque = a => ({
    ...a,
    idParam: a.ID_PARAMETRO,
    param: a.DESCRIZIONE_PARAMETRO,
    value: a.valore,
    date: parseDDMMYYYYDate(a.FECHA_HORA)
})

const parseDDMMYYYYDate = ddmmyyyy => {
    const [dd, mm, yyyy] = ddmmyyyy.split('/')
    return new Date([yyyy, mm, dd].join('-'))
}

/**
 * @param {Date} t1
 * @param {Date} t2
 * @returns -1 if t1 < t2, 0 if t1 == t2, 1 if t1 > t2
 */
const compareDates = (t1, t2) => Math.sign(t2.getTime() - t1.getTime())

class PiezometriService {

    /**
     * @private
     * @returns {Promise<Piezometro[]>}
     */
    async _getAllPiezometri() {
        return (await import('../data/piezometri/piezometri.json')).piezometri
            .map(toPiezometro)
    }

    /**
     * @private
     * @returns {Promise<Level[]>}
     */
    async _getAllLevels() {
        return (await import('../data/piezometri/liv_piezometrici.json')).liv_piezometrici
            .map(toLevel)
    }

    /**
     * @private
     * @returns {Promise<Conductivity[]>}
     */
    async _getConductivity() {
        return (await import('../data/piezometri/cond_elettrica.json')).cond_elettrica
            .map(toConductivity)
    }

    /**
     * @private
     * @returns {Promise<Acque[]>}
     */
    async _getAcque() {
        return (await import('../data/piezometri/acque_sotterranee.json')).acque_sotterranee
            .map(toAcque)
    }

    /**
     * @returns {Promise<Piezometro[]>}
     */
    async getAll() {
        return this._getAllPiezometri()
    }

    /**
     * @param {string} idSubarea
     * @returns {Promise<Piezometro[]>}
     */
    async getBySubarea(idSubarea) {
        return (await this._getAllPiezometri())
            .filter(p => p.subarea === idSubarea)
    }

    /**
     * @param {string} idPiezometro
     * @param {Date} fromDate
     * @param {Date} toDate
     * @param {any} measureType not implemented yet
     * @param {any} levelType not implemented yet
     * @returns {Promise<Level[]>}
     */
    async getLevels(idPiezometro, fromDate, toDate, measureType, levelType) {
        const piezometro = (await this._getAllPiezometri()).find(p => p.id === idPiezometro)
        const levels = await this._getAllLevels()
        if (isNaN(fromDate.getTime())) {
            fromDate = new Date(0)
        }

        if (isNaN(toDate.getTime())) {
            toDate = new Date('2100-10-05T14:48:00.000Z')
        }
        console.log('get levels', fromDate, toDate)

        const result = levels.filter(l =>
            l.IDpuntoDiMisura === piezometro.idpuntodimisura &&
            compareDates(fromDate, l.date) >= 0 &&
            compareDates(l.date, toDate) >= 0
        )

        return result
    }

    async getConductivity(idPiezometro, fromDate, toDate) {
        const piezometro = (await this._getAllPiezometri()).find(p => p.id === idPiezometro)
        const conductivity = await this._getConductivity()
        const result = conductivity.filter(c =>
            c.IDpuntoDiMisura === piezometro.idpuntodimisura &&
            compareDates(fromDate, c.date) >= 0 &&
            compareDates(c.date, toDate) >= 0
        )

        return result
    }

    /**
     * @param {string} idPiezometro
     * @returns {Promise<Conductivity>}
     */
    async lastConductivity(idPiezometro) {
        const piezometro = (await this._getAllPiezometri()).find(p => p.id === idPiezometro)
        const conductivity = (await this._getConductivity()).filter(c =>
            c.IDpuntoDiMisura === piezometro.idpuntodimisura
        )
        const result = conductivity.length > 0
            ? conductivity.reduce((acc, cur) => compareDates(acc.date, cur.date) < 0 ? acc : cur)
            : null

        return result
    }

    /**
     * @param {string} idPiezometro
     * @returns {Promise<Level>}
     */
    async lastLevels(idPiezometro) {
        const piezometro = (await this._getAllPiezometri()).find(p => p.id === idPiezometro)
        const levels = (await this._getAllLevels()).filter(l =>
            l.IDpuntoDiMisura === piezometro.idpuntodimisura
        )
        const result = levels.size > 0
            ? levels.reduce(
                (acc, cur) => compareDates(acc.date, cur.date) < 0 ? acc : cur
            )

            : null

        return result
    }

    /**
     * @param {string} idPiezometro
     * @param {Date} fromDate
     * @param {Date} toDate
     */
    async getAcque(idPiezometro, fromDate, toDate) {
        const piezometro = (await this._getAllPiezometri()).find(p => p.id === idPiezometro)
        const acque = await this._getAcque()
        const result = acque.filter(a =>
            a.IDpuntoDiMisura === piezometro.idpuntodimisura &&
            compareDates(fromDate, a.date) >= 0 &&
            compareDates(a.date, toDate) >= 0
        )

        const groups = Object.groupBy(result, ({ date }) => date.toDateString())
        return Object.entries(groups).map(([k, v]) => ({
            date: new Date(k),
            measures: v
        }))
    }

    async lastAcque(idPiezometro) {
        const piezometro = (await this._getAllPiezometri()).find(p => p.id === idPiezometro)
        const acque = (await this._getAcque()).filter(
            a => a.IDpuntoDiMisura === piezometro.idpuntodimisura
        )

        if (acque.length == 0) return null

        const groups = Object.groupBy(acque, ({ date }) => date.toDateString())
        const result = Object.entries(groups).map(([k, v]) => ({
            date: new Date(k),
            measures: v
        }))

        return result.reduce((acc, cur) =>
            compareDates(acc.date, cur.date) >= 0
                ? cur : acc
        )
    }
}


const piezometriService = new PiezometriService()
window.piezometriService = piezometriService

export { piezometriService }